import {
  Card,
  Space,
  Typography,
  Select,
  InputNumber,
  Tag,
  Button,
  Input,
  Row,
  Col,
  Divider,
  Empty,
} from "antd";

import {
  DeleteOutlined,
  CarOutlined,
  EnvironmentOutlined,
  ScheduleOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
} from "@ant-design/icons";

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  searchTransportRoutes,
} from "../../../services/quotationService";

const { Text, Title } = Typography;
const { TextArea } = Input;

const TransportCard = ({
  lead,
  day,
  updateDay,
  globalTransportConfig,
  vehicleOptions = [],
}) => {

  /* =========================================================
     TRANSPORT STATE
  ========================================================= */

  const transport =
    day.transport?.[0] || {};

  /* =========================================================
     LOCAL STATE
  ========================================================= */

  const [
    transportSearch,
    setTransportSearch,
  ] = useState(
    transport.routeName || ""
  );

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    transportResults,
    setTransportResults,
  ] = useState([]);

  /* =========================================================
     ROUTE KEY
  ========================================================= */

  const selectedRouteKey =
    transport.routeKey;

  /* =========================================================
     SEARCH ROUTES
  ========================================================= */

  useEffect(() => {

    const loadRoutes = async () => {

      if (
        !transportSearch?.trim()
      ) {

        setTransportResults([]);

        return;
      }

      try {

        setLoading(true);

        const destParam = Array.isArray(lead?.destinations) && lead.destinations.length
          ? lead.destinations.map(d => d.name || d.city || d).join(",")
          : (lead?.destination?.name || lead?.destination?.city || lead?.destination || "");

        const response =
          await searchTransportRoutes(
            transportSearch,
            lead?._id || null,
            destParam
          );

const data =

  Array.isArray(
    response?.data?.data
  )

    ? response.data.data

    : Array.isArray(
        response?.data
      )

      ? response.data

      : Array.isArray(
          response?.routes
        )

        ? response.routes

        : [];


        setTransportResults(data);

      } catch (error) {

        console.error(
          "TRANSPORT SEARCH ERROR:",
          error
        );

      } finally {

        setLoading(false);
      }
    };

    const timer =
      setTimeout(
        loadRoutes,
        400
      );

    return () =>
      clearTimeout(timer);

  }, [transportSearch]);

  /* =========================================================
     GROUP ROUTES
  ========================================================= */

  const groupedRoutes =
    useMemo(() => {

      const grouped = {};

      transportResults.forEach(
        (item) => {

const routeKey =
  `${(item.source || "")
    .trim()
    .toLowerCase()}__${(item.destination || "")
    .trim()
    .toLowerCase()}`;

          if (
            !grouped[routeKey]
          ) {

            grouped[
              routeKey
            ] = {

              routeKey,

routeName:
item.routeName ||
`${item.source} → ${item.destination}`,

              source:
                item.source,

              destination:
                item.destination,

              services: [],
            };
          }

          grouped[
            routeKey
          ].services.push(item);
        }
      );

      return grouped;

    }, [transportResults]);

  /* =========================================================
     ROUTE OPTIONS
  ========================================================= */

const routeOptions =
  Object.values(groupedRoutes)
    .sort((a, b) =>
      a.routeName.localeCompare(b.routeName)
    )
    .map((route) => ({
      label: route.routeName,
      value: route.routeKey,
    }));

  /* =========================================================
     SERVICE OPTIONS
  ========================================================= */

const serviceOptions = useMemo(() => {
  const services =
    groupedRoutes[selectedRouteKey]?.services || [];

  const seen = new Set();

  return services
    .filter((item) => {
      const key =
(item.serviceType || item.service || "")
.trim()
.toLowerCase();

      if (!key) return false;

      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    })
    .map((item) => ({
      label: item.serviceType || item.service,
      value: item.serviceType || item.service,
    }));
}, [groupedRoutes, selectedRouteKey]);

  /* =========================================================
     VEHICLE OPTIONS
  ========================================================= */

  const vehicleSelectOptions = useMemo(() => {
    const options = [];
    const seen = new Set();

    if (Array.isArray(transport.vehiclePricing)) {
      transport.vehiclePricing.forEach((item) => {
        if (item.vehicleType && !seen.has(item.vehicleType)) {
          seen.add(item.vehicleType);
          options.push({
            label: `${item.vehicleType} - ₹${item.price}`,
            value: item.vehicleType,
          });
        }
      });
    }

    const defaultVehicles = [
      "Sedan",
      "SUV",
      "Innova",
      "Innova Crysta",
      "Tempo Traveller",
      "Mini Bus",
      "Luxury Coach",
      ...(vehicleOptions || []),
    ];

    defaultVehicles.forEach((v) => {
      if (v && !seen.has(v)) {
        seen.add(v);
        options.push({
          label: v,
          value: v,
        });
      }
    });

    return options;
  }, [transport.vehiclePricing, vehicleOptions]);

  /* =========================================================
     SELECT ROUTE
  ========================================================= */

  const handleSelectRoute =
    (routeKey) => {

      const route =
        groupedRoutes[
          routeKey
        ];

      if (!route) return;

setTransportSearch(route.routeName);

setTransportResults(route.services);

      updateDay(day.id, {

        transport: [
          {

            ...transport,

            routeKey,

            routeName:
              route.routeName,

            source:
              route.source,

            destination:
              route.destination,

            serviceType: null,

            vehicle: "",

            itinerary: "",

            vehiclePricing: [],

            distance: "",

            duration: "",

            daySchedule: "",

            price: 0,
          },
        ],
      });
    };

  /* =========================================================
     SELECT SERVICE
  ========================================================= */

  const handleSelectService =
    (serviceType) => {

      const route =
        groupedRoutes[
          selectedRouteKey
        ];

      if (!route) return;

const selectedService =
  route.services.find(
    (item) =>
      (item.serviceType || item.service || "")
        .trim()
        .toLowerCase() ===
      (serviceType || "")
        .trim()
        .toLowerCase()
  );

      if (
        !selectedService
      ) return;

      const firstVehicle =
        selectedService
          ?.vehiclePricing?.[0];

      updateDay(day.id, {

        transport: [
          {

            ...transport,

            routeId:
              selectedService._id,

            serviceType:
               selectedService.serviceType ||

               selectedService.service,

            daySchedule:
              selectedService.daySchedule || "",

            itinerary:
              selectedService.daySchedule || "",

            distance:
              selectedService.distance || "",

            duration:
              selectedService.duration || "",

image:
    route.image ||
    route.routeImage ||
    selectedService?.image ||
    "",

            vehiclePricing:
              selectedService.vehiclePricing || [],

            vehicle:
              globalTransportConfig
                ?.vehicleType ||

              firstVehicle
                ?.vehicleType ||

              "",

            price:
              firstVehicle
                ?.price || 0,

total:
  firstVehicle
    ?.price || 0,

          },
        ],

        title:
          day.title ||
          selectedService.routeName ||

`${selectedService.source} → ${selectedService.destination}`
      });
    };

  /* =========================================================
     VEHICLE CHANGE
  ========================================================= */

  const handleVehicleChange =
    (vehicleType) => {

      const selectedVehicle =
        transport.vehiclePricing?.find(
          (item) =>
            item.vehicleType ===
            vehicleType
        );

const updatedPrice =
  transport.manualOverride
    ? Number(transport.manualPrice ?? transport.price ?? 0)
    : Number(
        selectedVehicle?.price ??
        transport.price ??
        0
      );

updateDay(day.id, {
  transport: [
    {
      ...transport,
      vehicle: vehicleType,
      vehicleType,
      price: updatedPrice,
      total: updatedPrice,
    },
  ],
});

 };

  /* =========================================================
     PRICE CHANGE
  ========================================================= */

const handlePriceChange = (value) => {
  const price = Number(value ?? 0);

  updateDay(day.id, {
    transport: [
      {
        ...transport,
        price,
        total: price,
        manualPrice: price,
        manualOverride: true,
      },
    ],
  });
};

  /* =========================================================
     ITINERARY CHANGE
  ========================================================= */

  const handleItineraryChange =
    (e) => {

      updateDay(day.id, {

        transport: [
          {

            ...transport,

            itinerary:
              e.target.value,
          },
        ],
      });
    };

  /* =========================================================
     REMOVE TRANSPORT
  ========================================================= */

  const removeTransport =
    () => {

      setTransportSearch("");

      updateDay(day.id, {

        transport: [],
      });
    };

  /* =========================================================
     EMPTY STATE
  ========================================================= */

  const showDetails =
    !!transport.serviceType;

  return (

    <Card
      size="small"
      style={{
        borderRadius: 20,
        border:
          "1px solid #dbeafe",
        background:
          "#fafcff",
      }}
      bodyStyle={{
        padding: 20,
      }}
    >

      <Space
        direction="vertical"
        size={20}
        style={{
          width: "100%",
        }}
      >

        {/* =====================================================
           HEADER
        ===================================================== */}

        <Space align="center">

          <CarOutlined
            style={{
              fontSize: 18,
              color: "#1677ff",
            }}
          />

          <Title
            level={5}
            style={{
              margin: 0,
            }}
          >
            Routing & Transport
          </Title>

        </Space>

        {/* =====================================================
           ROUTE SEARCH
        ===================================================== */}

        <div>

          <Text strong>
            Route
          </Text>

<Select
  showSearch

  size="large"

  style={{
    width: "100%",
    marginTop: 8,
  }}

  placeholder="Search route like Delhi → Manali"

  value={
    transport.routeKey ||
    undefined
  }

  filterOption={false}

  loading={loading}

  onSearch={(value) => {

    setTransportSearch(value);
  }}

  onChange={(value) => {

    handleSelectRoute(value);
  }}

  options={routeOptions}

  suffixIcon={
    <EnvironmentOutlined />
  }
/>


        </div>

        {/* =====================================================
           SERVICE TYPE
        ===================================================== */}

        {!!selectedRouteKey && (

          <div>

            <Text strong>
              Service Type
            </Text>

            <Select
              style={{
                width: "100%",
                marginTop: 8,
              }}

              size="large"

              placeholder="Select service type"

              value={
                transport.serviceType ||
                undefined
              }

              options={
                serviceOptions
              }

              onChange={
                handleSelectService
              }
            />

          </div>

        )}

        {/* =====================================================
           DETAILS
        ===================================================== */}

        {showDetails ? (

          <Card
            size="small"
            style={{
              borderRadius: 16,
              border:
                "1px solid #edf2f7",
              background:
                "#ffffff",
            }}
          >

            <Space
              direction="vertical"
              size={18}
              style={{
                width: "100%",
              }}
            >

              {/* =========================================
                  SUMMARY
              ========================================= */}

              <Row gutter={16}>

                <Col span={12}>

                  <Text strong>
                    Route
                  </Text>

                  <div
                    style={{
                      marginTop: 6,
                    }}
                  >
                    {
                      transport.source
                    }
                    {" → "}
                    {
                      transport.destination
                    }
                  </div>

                </Col>

                <Col span={12}>

                  <Text strong>
                    Service
                  </Text>

                  <div
                    style={{
                      marginTop: 6,
                    }}
                  >
                    {
                      transport.serviceType
                    }
                  </div>

                </Col>

              </Row>

              {/* =========================================
                  TAGS
              ========================================= */}

              <Space wrap>

                {!!transport.distance && (

                  <Tag
                    icon={
                      <DashboardOutlined />
                    }
                    color="blue"
                  >
                    {
                      transport.distance
                    } km
                  </Tag>

                )}

                {!!transport.duration && (

                  <Tag
                    icon={
                      <ClockCircleOutlined />
                    }
                    color="purple"
                  >
                    {
                      transport.duration
                    }
                  </Tag>

                )}

                {!!transport.daySchedule && (

                  <Tag
                    icon={
                      <ScheduleOutlined />
                    }
                    color="gold"
                  >
                    {
                      transport.daySchedule
                    }
                  </Tag>

                )}

              </Space>

              <Divider
                style={{
                  margin:
                    "4px 0",
                }}
              />

              {/* =========================================
                  VEHICLE
              ========================================= */}

              <div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text strong>
                    Vehicle Type
                  </Text>
                  {globalTransportConfig?.vehicleType && globalTransportConfig?.applySameCab && !transport.vehicle && (
                    <Tag color="blue" style={{ borderRadius: 6, fontSize: 11 }}>
                      Global Default: {globalTransportConfig.vehicleType}
                    </Tag>
                  )}
                </div>

                <Select
                  showSearch
                  style={{
                    width: "100%",
                    marginTop: 8,
                  }}

                  size="large"

                  value={
                    transport.vehicle ||
                    (globalTransportConfig?.applySameCab ? globalTransportConfig?.vehicleType : undefined) ||
                    undefined
                  }

                  placeholder="Select vehicle type for this day"

                  options={
                    vehicleSelectOptions
                  }

                  onChange={
                    handleVehicleChange
                  }
                />

              </div>

              {/* =========================================
                  ITINERARY
              ========================================= */}

              <div>

                <Text strong>
                  Day Schedule / Itinerary
                </Text>

                <TextArea
                  rows={6}

                  value={
                    transport.itinerary || ""
                  }

                  placeholder="Detailed transport itinerary"

                  style={{
                    marginTop: 8,
                    borderRadius: 12,
                  }}

                  onChange={
                    handleItineraryChange
                  }
                />

              </div>

              {/* =========================================
                  PRICE
              ========================================= */}

              <div>

                <Text strong>
                  Transport Price
                </Text>

                <InputNumber
                  min={0}

                  prefix="₹"

                  style={{
                    width: "100%",
                    marginTop: 8,
                  }}

                  size="large"

                  value={
                    transport.price || 0
                  }

                  onChange={
                    handlePriceChange
                  }
                />

              </div>

              {/* =========================================
                  REMOVE BUTTON
              ========================================= */}

              <Button
                danger

                icon={
                  <DeleteOutlined />
                }

                onClick={
                  removeTransport
                }
              >
                Remove Transport
              </Button>

            </Space>

          </Card>

        ) : (

          <Card
            size="small"
            style={{
              borderRadius: 14,
              border:
                "1px dashed #d1d5db",
              background:
                "#fff",
            }}
          >

            <Empty
              image={
                Empty.PRESENTED_IMAGE_SIMPLE
              }

              description="Select route and service type to build itinerary"
            />

          </Card>

        )}

      </Space>

    </Card>
  );
};

export default TransportCard;
